// One card on the exhibition QR board (#4) — read-only: the entity's kind,
// cover thumbnail, title, 3-digit visitor code, and its QR image (or a "publish
// to generate" note when the QR hasn't been produced yet). An optional Open
// link jumps to the entity's visitor page (the URL the QR encodes).

import type { FC } from "react";
import { ExternalLink, ImageOff } from "lucide-react";

export interface QRCardProps {
  kindLabel: string;
  title: string;
  code: string;
  qrCodeUrl?: string | null;
  coverImageUrl?: string | null;
  /** Visitor page URL for this entity; renders an Open link when set. */
  openHref?: string;
  /** Root testid override so a board of cards stays addressable. */
  testId?: string;
}

const QRCard: FC<QRCardProps> = ({
  kindLabel,
  title,
  code,
  qrCodeUrl,
  coverImageUrl,
  openHref,
  testId = "qr-card",
}) => (
  <div
    className="flex flex-col items-center gap-2 p-4 bg-white border border-border rounded-lg text-center break-inside-avoid"
    data-testid={testId}
  >
    <span className="text-xs uppercase tracking-wide text-muted-foreground">
      {kindLabel}
    </span>

    {coverImageUrl ? (
      <img
        src={coverImageUrl}
        alt={`Cover of ${title}`}
        className="w-16 h-16 object-cover rounded"
        data-testid="qr-card-cover"
      />
    ) : (
      <div className="w-16 h-16 flex items-center justify-center rounded bg-muted/40 text-muted-foreground">
        <ImageOff className="w-5 h-5" />
      </div>
    )}

    <span className="font-medium text-sm truncate max-w-full" title={title}>
      {title}
    </span>

    {qrCodeUrl ? (
      <img
        src={qrCodeUrl}
        alt={`QR code for ${title}`}
        className="w-32 h-32 object-contain"
        data-testid="qr-card-img"
      />
    ) : (
      <div
        className="w-32 h-32 flex items-center justify-center text-xs text-muted-foreground bg-muted/40 rounded"
        data-testid="qr-card-missing"
      >
        No QR yet — publish to generate
      </div>
    )}

    <span
      className="font-mono text-2xl font-bold tracking-widest text-primary"
      data-testid="qr-card-code"
    >
      {code}
    </span>

    {openHref && (
      <a
        href={openHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        data-testid="qr-card-open"
      >
        <ExternalLink className="w-3 h-3" />
        Open page
      </a>
    )}
  </div>
);

export default QRCard;
